// "When it goes" (U12), chosen at the last look before a send (a card's send strip and the
// composer's review): now (after the usual few seconds to undo), one of a few presets, or a time
// picked or typed on the owner's clock. A scheduled send is approved exactly like one that goes
// now; at its time MeeMail checks the thread again, and if someone wrote in the meantime it comes
// back for a look instead of going out.
import { useMemo, useState } from "react";
import { Clock } from "lucide-react";
import { cn } from "@web/lib/cn";
import { zoneLabel } from "@web/lib/timeZones";
import { useNow } from "@web/lib/useNow";
import { useSendZone } from "./sendLaterApi";
import type { SendWhen } from "./sendLaterStore";
import {
  capitalize,
  fromWallInput,
  parseWhen,
  sendAtProblem,
  sendAtWords,
  sendPresets,
  wallInputValue,
  type SendPreset,
} from "./when";

const CHOICE =
  "h-8 rounded-md border px-2.5 text-sm text-ink-2 disabled:opacity-60 aria-checked:border-accent/60 aria-checked:bg-accent-soft aria-checked:text-accent-text";

function Choice({
  checked,
  disabled,
  onClick,
  children,
  testId,
}: {
  checked: boolean;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
  testId?: string;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      disabled={disabled}
      onClick={onClick}
      data-testid={testId}
      className={cn(CHOICE, !checked && "border-line-strong bg-paper hover:bg-sunk")}
    >
      {children}
    </button>
  );
}

/** The date-time field and the typed phrase, both read on the owner's clock. */
function CustomTime({
  value,
  zone,
  now,
  disabled,
  onChange,
}: {
  value: SendWhen;
  zone: string;
  now: number;
  disabled: boolean;
  onChange: (next: SendWhen) => void;
}) {
  const [typed, setTyped] = useState("");
  const [unread, setUnread] = useState(false);
  const read = async (e?: { preventDefault: () => void }) => {
    e?.preventDefault();
    if (!typed.trim()) return;
    const at = await parseWhen(typed, Date.now(), zone).catch(() => null);
    setUnread(!at);
    if (at) {
      onChange(at);
      setTyped("");
    }
  };
  return (
    <div className="flex flex-col gap-1.5 pl-6.5">
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="datetime-local"
          aria-label="Send at (your time)"
          value={value ? wallInputValue(value, zone) : ""}
          min={wallInputValue(new Date(now).toISOString(), zone)}
          disabled={disabled}
          onChange={(e) => onChange(fromWallInput(e.target.value, zone) ?? "")}
          className="h-9 rounded-md border border-line-strong bg-paper px-2 text-sm text-ink"
          data-testid="send-later-input"
        />
        <input
          type="text"
          aria-label="Or type when"
          placeholder="or type: fri 9am"
          value={typed}
          disabled={disabled}
          onChange={(e) => {
            setTyped(e.target.value);
            setUnread(false);
          }}
          onBlur={() => void read()}
          onKeyDown={(e) => {
            if (e.key === "Enter") void read(e);
          }}
          className="h-9 min-w-0 flex-1 rounded-md border border-line-strong bg-paper px-2 text-sm text-ink placeholder:text-faint"
        />
      </div>
      {unread ? (
        <p className="text-sm text-note-ink">
          Couldn&rsquo;t read that as a time. Try &ldquo;tomorrow 8:30&rdquo;.
        </p>
      ) : null}
    </div>
  );
}

/** The confirm button's words for a send choice, and why it can't be pressed yet (if so). */
export function useSendLabel(value: SendWhen, label: string) {
  const zone = useSendZone();
  const now = useNow(30_000);
  if (value === null) return { label, problem: null };
  const problem = sendAtProblem(value, now);
  return {
    label: problem ? "Pick a time first" : `Send ${sendAtWords(value, now, zone)}`,
    problem,
  };
}

export function SendLaterPicker({
  value,
  onChange,
  disabled,
  reply = true,
}: {
  /** null: now (after the undo window); "": a time is being picked; else the chosen time. */
  value: SendWhen;
  onChange: (next: SendWhen) => void;
  disabled: boolean;
  /** It answers a thread (a new reply there brings it back instead of sending). */
  reply?: boolean;
}) {
  const zone = useSendZone();
  const now = useNow(30_000);
  const presets = useMemo(() => sendPresets(now, zone), [now, zone]);
  const preset: SendPreset | undefined = presets.find((p) => p.at === value);
  // "Pick a time" stays open once chosen; a time that is not a preset (a "Change") opens it too.
  const [picking, setCustom] = useState(false);
  const custom = picking || (value !== null && !preset);
  const problem = sendAtProblem(value, now);
  const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return (
    <div
      className="flex flex-col gap-2 rounded-lg border border-line bg-paper px-3 py-2.5"
      data-testid="send-later"
    >
      <p className="inline-flex items-center gap-1.5 text-[15px] font-medium text-ink">
        <Clock aria-hidden className="size-4 text-muted" />
        When it goes
      </p>
      <div role="radiogroup" aria-label="When it goes" className="flex flex-wrap gap-1.5 pl-6.5">
        <Choice
          checked={value === null}
          disabled={disabled}
          onClick={() => {
            setCustom(false);
            onChange(null);
          }}
          testId="send-later-now"
        >
          Now
        </Choice>
        {presets.map((p) => (
          <Choice
            key={p.id}
            checked={!custom && value === p.at}
            disabled={disabled}
            onClick={() => {
              setCustom(false);
              onChange(p.at);
            }}
            testId={`send-later-${p.id}`}
          >
            {p.label}
          </Choice>
        ))}
        <Choice
          checked={custom}
          disabled={disabled}
          onClick={() => {
            setCustom(true);
            if (value === null || preset) onChange("");
          }}
          testId="send-later-custom"
        >
          Pick a time…
        </Choice>
      </div>
      {custom ? (
        <CustomTime value={value} zone={zone} now={now} disabled={disabled} onChange={onChange} />
      ) : null}
      <p className={cn("pl-6.5 text-sm", problem ? "text-note-ink" : "text-muted")} role="status">
        {problem
          ? problem
          : value
            ? `${capitalize(sendAtWords(value, now, zone))}${
                browserZone && browserZone !== zone ? ` (${zoneLabel(zone)})` : ""
              }.${
                reply
                  ? " If someone writes in the thread first, it comes back to you instead of going out."
                  : " You can cancel it until then."
              }`
            : "Right after you confirm, with a few seconds to undo."}
      </p>
    </div>
  );
}
